import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './api';
import i18n from '@/i18n';
import type { Homework, HomeworkExercise } from '../types/api';

export function useHomeworks(childId: string) {
  return useQuery({
    queryKey: ['child', childId, 'homeworks'],
    queryFn: () => apiFetch<{ homeworks: Homework[] }>(`/children/${childId}/homeworks`),
    enabled: !!childId,
    // Poll while any homework's photo OCR is still running in the background
    // (see lib/homework_ocr_trigger.py) so real exercises replace the
    // theme-based placeholder without the user needing to refresh manually.
    refetchInterval: (query) => {
      const data = query.state.data as { homeworks: Homework[] } | undefined;
      const hasPending = data?.homeworks.some((h) => h.ocr_status === 'pending');
      return hasPending ? 4000 : false;
    },
  });
}

export function useCreateHomework(childId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      subject: string;
      themes: string[];
      image_base64?: string;
      media_type?: 'image/jpeg' | 'image/png' | 'image/webp' | 'application/pdf';
    }) =>
      apiFetch<{
        homework_id: string;
        subject: string;
        exercises: HomeworkExercise[];
        ocr_status?: Homework['ocr_status'];
      }>(`/children/${childId}/homeworks`, { method: 'POST', body: JSON.stringify(payload) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['child', childId, 'homeworks'] }),
  });
}

export function useCompleteExercise(childId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      homeworkId,
      exerciseId,
      status,
    }: {
      homeworkId: string;
      exerciseId: string;
      status: 'pending' | 'done';
    }) =>
      apiFetch<{ exercise_id: string; status: string; homework_status: string }>(
        `/children/${childId}/homeworks/${homeworkId}/exercises/${exerciseId}`,
        { method: 'PUT', body: JSON.stringify({ status }) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['child', childId, 'homeworks'] }),
  });
}

export function useDeleteHomework(childId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (homeworkId: string) =>
      apiFetch<{ deleted: string }>(`/children/${childId}/homeworks/${homeworkId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['child', childId, 'homeworks'] }),
  });
}

export function useHomeworkDocument(childId: string, homeworkId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['child', childId, 'homework', homeworkId, 'document'],
    queryFn: () =>
      apiFetch<{ document_url: string; media_type: string }>(
        `/children/${childId}/homeworks/${homeworkId}/document`,
      ),
    enabled: !!childId && !!homeworkId && enabled,
    staleTime: 50 * 60 * 1000, // presigned URL valid 1 hr; refetch at 50 min
  });
}

export function useHomeworkHelp() {
  return useMutation({
    mutationFn: (payload: {
      homework_id: string;
      exercise_id: string;
      message: string;
      history?: { role: 'user' | 'assistant'; content: string }[];
    }) => apiFetch<{ reply: string }>('/ai/homework-help', {
      method: 'POST',
      body: JSON.stringify({ ...payload, lang: i18n.language.slice(0, 2) }),
    }),
  });
}
